import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: 'default' | 'ghost' | 'danger'
  size?: 'sm' | 'icon'
}

const VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  default:
    'border-border bg-surface text-fg hover:border-border-strong hover:bg-surface-raised',
  // Reads as plain text until pointed at, so a row of them adds no visual weight.
  ghost: 'border-transparent bg-transparent text-fg-muted hover:bg-surface hover:text-fg',
  danger: 'border-border bg-surface text-danger hover:border-danger',
}

const SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-[26px] px-2.5',
  icon: 'h-[26px] w-[26px] p-0',
}

export function Button({
  className,
  variant = 'default',
  size = 'sm',
  type = 'button',
  ...props
}: ButtonProps): ReactNode {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border text-[12px] leading-none whitespace-nowrap transition-colors',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  )
}
